// services/graphUserService.js — Microsoft Graph API for O365 user lookups
import { userSearchRequest, peopleRequest } from "../authConfig";

const graphBase = "https://graph.microsoft.com/v1.0";

function normalizeGraphUser(u) {
  return {
    id: u.id || "",
    name: u.displayName || "",
    email: u.mail || u.userPrincipalName || "",
    jobTitle: u.jobTitle || "",
    department: u.department || "",
  };
}

function normalizePerson(p) {
  const email = p.scoredEmailAddresses?.[0]?.address || p.userPrincipalName || "";
  return {
    id: p.id || "",
    name: p.displayName || "",
    email,
    jobTitle: p.jobTitle || "",
    department: p.department || "",
  };
}

/**
 * Get the current signed-in user's full O365 profile.
 * Uses the existing User.Read scope — no extra consent needed.
 */
export async function getCurrentUserProfile(token) {
  const res = await fetch(
    `${graphBase}/me?$select=id,displayName,mail,userPrincipalName,jobTitle,department`,
    { headers: { Authorization: `Bearer ${token}` } },
  );
  if (!res.ok) throw new Error("Failed to fetch current user profile");
  return normalizeGraphUser(await res.json());
}

/**
 * Search O365 users by name or email.
 *
 * Strategy 1 — /users with User.ReadBasic.All (full directory, silent consent only)
 * Strategy 2 — /me/people with People.Read (social graph — people you work with)
 * Strategy 3 — returns { users: [], searchMode: "failed" } so UI falls back to manual entry
 *
 * Returns: { users: NormalisedUser[], searchMode: "directory" | "people" | "failed" }
 */
export async function searchO365Users(instance, accounts, query) {
  const q = (query || "").trim();
  if (q.length < 2) return { users: [], searchMode: null };

  const account = accounts[0];

  // ── Strategy 1: /users — full Azure AD directory search ────────────────────
  try {
    const tokenRes = await instance.acquireTokenSilent({ ...userSearchRequest, account });
    const url =
      `${graphBase}/users` +
      `?$search="displayName:${q}" OR "mail:${q}"` +
      `&$select=id,displayName,mail,userPrincipalName,jobTitle,department` +
      `&$top=15&$count=true`;

    const res = await fetch(url, {
      headers: {
        Authorization: `Bearer ${tokenRes.accessToken}`,
        ConsistencyLevel: "eventual",
      },
    });

    if (res.ok) {
      const data = await res.json();
      const users = (data.value || [])
        .map(normalizeGraphUser)
        .filter((u) => u.email); // drop accounts with no email
      return { users, searchMode: "directory" };
    }
  } catch {
    // Token not yet consented — fall through to next strategy
  }

  // ── Strategy 2: /me/people — user's social/recent contacts ─────────────────
  try {
    const tokenRes = await instance.acquireTokenSilent({
      ...peopleRequest,
      account,
    }).catch(() => instance.acquireTokenPopup({ ...peopleRequest, account }));

    const res = await fetch(
      `${graphBase}/me/people` +
        `?$search="${encodeURIComponent(q)}"` +
        `&$select=id,displayName,scoredEmailAddresses,jobTitle,department` +
        `&$top=15`,
      { headers: { Authorization: `Bearer ${tokenRes.accessToken}` } },
    );

    if (res.ok) {
      const data = await res.json();
      const users = (data.value || [])
        .map(normalizePerson)
        .filter(
          (u) =>
            u.email &&
            (u.name.toLowerCase().includes(q.toLowerCase()) ||
              u.email.toLowerCase().includes(q.toLowerCase())),
        );
      return { users, searchMode: "people" };
    }
  } catch {
    // Consent denied or network error
  }

  return { users: [], searchMode: "failed" };
}

/**
 * Fetch all unique department strings from the Azure AD directory.
 * Returns a sorted array of non-empty, uppercased department names.
 * Falls back to [] silently if the token cannot be acquired.
 */
export async function fetchOrgDepartments(instance, accounts) {
  const account = accounts[0];
  try {
    const tokenRes = await instance.acquireTokenSilent({ ...userSearchRequest, account });
    const res = await fetch(
      `${graphBase}/users?$select=department&$top=999&$count=true`,
      {
        headers: {
          Authorization: `Bearer ${tokenRes.accessToken}`,
          ConsistencyLevel: "eventual",
        },
      },
    );
    if (!res.ok) return [];
    const data = await res.json();
    return [
      ...new Set(
        (data.value || [])
          .map((u) => (u.department || "").trim().toUpperCase())
          .filter(Boolean),
      ),
    ].sort();
  } catch {
    return [];
  }
}
